
import React from 'react';
import PropTypes from 'prop-types';
import Relay from 'react-relay';
import {Link} from 'react-router';
import {injectIntl, FormattedMessage, FormattedDate, IntlShape} from 'react-intl';
import {FlowView} from './FlowView.react';

import urlMapping from '../app/URLMapping';
import {wrapNotFound} from "../app/NotFoundHandler.react";
import './flowSimpleList.scss';
import ScenarioIcon, {MEDIUM_SIZE} from '../scenarios/ScenarioIcon.react';
import FlowIcon from '../scenarios/FlowIcon.react';
import {getLastOrganisationId} from '../app/LastOrganisationId';
import {getFlowTitle} from './FlowConfig.react';
import LoadingButton from '../fw/LoadingButton.react';
import * as BrowserPermission from '../BrowserPermission';
import { Organisation } from '../../common/types/types';

export function getStrategyForFlow(organisation, flow) {
    let strategy = flow.versionStrategy;
    if (!strategy && !flow.flowDefVersion) {
        return " [development]";
    }

    if (strategy && !flow.flowDefVersion) {
        return " [" + strategy + "]";
    }

    if (flow.flowDef && flow.flowDef.versionConfig && flow.flowDefVersion) {
        let versionConfig = JSON.parse(flow.flowDef.versionConfig);
        if (flow.flowDefVersion.realId && flow.flowDefVersion.realId === versionConfig[strategy]) {
            return " [" + strategy + "]";
        }
    }

    if (strategy) {
        return " [" + strategy + "]";
    }

    if (flow.flowDefVersion && flow.flowDefVersion.strategy) {
        return " [" + flow.flowDefVersion.strategy + "]";
    }
}

interface FlowSimpleListProps {
    filter?: (...args: unknown[]) => unknown;
    intl: IntlShape;
    organisation: Organisation;
    relay?: object;
}

class FlowSimpleList extends React.Component<FlowSimpleListProps> {

    typeName = 'flows';

    static propTypes = {
        filter: PropTypes.func,
        intl: PropTypes.object.isRequired,
        organisation: PropTypes.object.isRequired,
        relay: PropTypes.object
    };

    constructor(props) {
        super(props);

        this.state = {
            sortType: "type",
            hideBlocked: false,
            groupName: '',
        };
    }

    render() {
        const {flows} = this.props.organisation;
        let list = this.props.filter ? {edges: flows.edges.filter(({node}) => this.props.filter(node))} : flows;

		// filter out deleted flows
		list = {edges: list.edges.filter(({node}) => !node.deleted)};

        if (list.edges.length === 0) {
            return null;
        }

        list = list.edges.map(({node}) => node);

        if (this.state.sortType === 'name') {
            list = list.sort((a, b) => {
                let name1 = (a.name || a.groupName || (a.flowDef ? a.flowDef.name : '')) || '';
                let name2 = (b.name || b.groupName || (b.flowDef ? b.flowDef.name : '')) || '';
                return name1.localeCompare(name2);
            });
        } else if (this.state.sortType === 'type') {
            list = list.sort((a, b) => {
                let name1 = ((a.flowDef ? a.flowDef.name : '') || a.groupName || a.name) || '';
                let name2 = ((b.flowDef ? b.flowDef.name : '') || b.groupName || b.name) || '';
                return name1.localeCompare(name2);
            });
        }

        let groupList = [];
        let groupCount = {
            "_all": 0,
            "_disabled": 0,
            "_noGroup": 0};
        for (let flow of list) {
            groupCount['_all']++;
            if (flow.groupName) {
                if (!groupCount[flow.groupName]) {
                    groupCount[flow.groupName] = 0;
                }

                groupCount[flow.groupName]++;

                groupList.push(flow.groupName);
            } else {
                groupCount['_noGroup']++;
            }

            if (!flow.enabled) {
                groupCount['_disabled']++;
            }
        }

        if (this.state.groupName) {
            list = list.filter((a) => {
                if (this.state.groupName === "_noGroup" && !a.groupName) {
                    return true;
                }
                return this.state.groupName === a.groupName;
            });
        }

        return (
            <div>
                <div className="sectionHeader">
                    <h3><FormattedMessage defaultMessage="Active Flows:" id="channelList.activeFlows" /></h3>
                    <div className="btn-toolbar" role="toolbar" style={{"float": "right"}}>
                        <div aria-label="..." className="btn-group" role="group">
                            <LoadingButton className={"btn btn-xs btn-default " + (this.state.sortType === 'type' ? 'active' : '')} onClick={() => this.setState({'sortType': 'type'})}><FormattedMessage defaultMessage="By Type" id="flowList.type" /></LoadingButton>
                            <LoadingButton className={"btn btn-xs btn-default " + (this.state.sortType === 'name' ? 'active' : '') } onClick={() => this.setState({'sortType': 'name'})}><FormattedMessage defaultMessage="By own name" id="flowList.name2" /></LoadingButton>
                            {groupCount['_disabled'] > 0 ? <LoadingButton className={"btn btn-xs btn-default " + (this.state.hideBlocked === true ? 'active' : '')} onClick={() => this.setState({'hideBlocked': !this.state.hideBlocked})}>{this.state.hideBlocked ? <FormattedMessage defaultMessage="Show blocked" id="flowList.showBlocked" /> : <FormattedMessage defaultMessage="Hide blocked" id="flowList.hideBlocked" />} ({groupCount['_disabled']})</LoadingButton> : null}
                        </div>
                    </div>
                </div>

                {groupList.length > 0 ?
                    <div className="btn-toolbar" role="toolbar" style={{"paddingBottom": ".5em"}}>
                        <div aria-label="..." className="btn-group" role="group">
                            <LoadingButton className={"btn btn-xs btn-default " + (!this.state.groupName ? 'active' : '')} onClick={() => this.setState({'groupName': ''})}><FormattedMessage defaultMessage="All" id="flowList.all" /> ({groupCount['_all']})</LoadingButton>
                            <LoadingButton className={"btn btn-xs btn-default " + (this.state.groupName === "_noGroup" ? 'active' : '')} onClick={() => this.setState({'groupName': '_noGroup'})}><FormattedMessage defaultMessage="No group" id="flowList.noGroup" /> ({groupCount['_noGroup']})</LoadingButton>
                            {groupList.map((groupName) => {
                                return (
                                    <LoadingButton className={"btn btn-xs btn-default " + (this.state.groupName === groupName ? 'active' : '')}  key={groupName} onClick={() => this.setState({'groupName': groupName})}>{groupName} ({groupCount[groupName]})</LoadingButton>
                                );
                            })}
                        </div>
                    </div>
                : null}

                <div className="flowGridList">
                    {list.map((node, idx) => {
                        let flow = node;
                        let status = null;

                        if (flow.deleted) {
                            return null;
                        }

                        if (this.state.hideBlocked && !flow.enabled) {
                            return null;
                        }

                        status = FlowView.getStatusMessage(flow, this.props.intl);

                        let flowTitle = getFlowTitle(flow);
                        let version = "";

                        if (BrowserPermission.developerModeEnabled()) {
                            version = flow.flowDefVersion && flow.flowDefVersion.length !== 0 ? <div className="xs-hide" style={{"float": "right", "padding-top": "4px"}}><small style={{"color": "#ccc"}} to={urlMapping.getUrl('flowDefVersions', "view", flow.flowDefVersion)}><FormattedMessage defaultMessage="Version:" id="flow.flowDefVersion" /> <FormattedDate value={flow.flowDefVersion.createdAt}/>${flow.flowDefVersion.gitTag.substring(0, 3) + (getStrategyForFlow(this.props.organisation, flow))}</small></div> : <div className='xs-hide' style={{"float": "right", "padding-top": "4px"}}> <p><small style={{color: '#ff0000'}}>Dev version</small></p></div>;
                        }

                        return (
                            <div className="flowGridItem" key={idx + (Date.now())}>
                                {FlowView.getBillingStatus(flow, this.props.intl)}
                                {version}
                                <Link to={urlMapping.getUrl('flows', 'view', flow)}>
                                    <div className="flowGridItem-icons">
										{flow.scenario ? <ScenarioIcon isFlow scenario={flow.scenario} size={MEDIUM_SIZE} /> : <FlowIcon flow={flow} size={MEDIUM_SIZE} />}
                                    </div>

                                    <div className="flowGridText layout-column">
										<span className="flex"></span>
                                        <strong className="flowGridHeader flex-nogrow">{flowTitle}</strong>
                                        {status ? (<span className="flowGridStatus flex-nogrow">{status}</span>) : null}
										<span className="flex"></span>
                                    </div>
                                </Link>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }
}

export default Relay.createContainer(wrapNotFound(injectIntl(FlowSimpleList), (props) => {
    return props.organisation;
}), {
    initialVariables: {
        first: 200,
        after: null,
        organisationId: '',
    },
    prepareVariables (variables) {
		if (!variables.organisationId) {
			variables.organisationId = getLastOrganisationId();
		}

        return variables;
    },
    fragments: {
        organisation: () => Relay.QL`
            fragment on Organisation {
                id,
                versionStrategy,
                flows(first: $first, after: $after, deleted: false) {
                    edges {
                        node {
                            id,
                            name,
                            groupName,
                            organisation_id,
                            instance_id,
                            description,
                            selectedChannels,
                            billingStatus,
                            activated,
                            deleted,
                            enabled,
                            paused,
                            status,
                            invalid,
                            hasInvalidChannel,
                            lastInvocationAt,
                            lastInvocationErrorAt,
                            lastInvocationError,
                            versionStrategy,
                            flowDefVersion {
                                id,
                                realId,
                                flowDef_id,
                                strategy,
                                createdAt,
                                description,
                                gitTag,
                            },
                            channels(first: 100) {
                                edges {
                                    node {
                                        id,
                                        realId,
                                        name,
                                        channelDef {
                                            id,
                                            realId,
                                            icon,
                                            name,
                                        }
                                    }
                                }
                            },
                            flowDef {
                                id,
                                name,
                                title,
                                state,
                                versionConfig,
                                globallyDisabled,
                            },
							scenario {
								${ScenarioIcon.getFragment('scenario')},
							},
                            ${FlowIcon.getFragment('flow')},
                        }
                    },
                    pageInfo {
                        hasNextPage,
                        hasPreviousPage,
                        startCursor,
                        endCursor
                    }
                }
            }
        `,
    },
});
